import first from 'lodash/first';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { makeValidateSync } from 'mui-rff';
import * as Yup from 'yup';
import { AnyObject, Maybe } from 'yup';
import { FieldLabels, FieldNames, FieldProp, FieldProps, RequiredFields, RffSchema } from './RffHelperTypes';

const isArraySchema = <SchemaShape extends Maybe<AnyObject>>(schema: unknown): schema is Yup.ArraySchema<Yup.ObjectSchema<SchemaShape>[], unknown> =>
    schema instanceof Yup.array;

const isObjectSchema = <SchemaShape extends Maybe<AnyObject>>(schema: unknown): schema is Yup.ObjectSchema<SchemaShape> =>
    !isEmpty((schema as Yup.AnyObjectSchema).fields);

type ReduceField<SchemaShape extends Maybe<AnyObject>, ReturnResult> = (
    previousValue: ReturnResult,
    currentValue: [string, Yup.AnySchema<SchemaShape>]
) => ReturnResult;

const iterateYupFields = <SchemaShape extends Maybe<AnyObject>, ReturnResult>(
    schema: Yup.AnySchema<SchemaShape>,
    callbackFn: ReduceField<SchemaShape, ReturnResult>,
    initialValue: ReturnResult
): ReturnResult => {
    const fields = (schema as Yup.ObjectSchema<SchemaShape>)?.fields;
    return fields ? Object.entries(fields).reduce(callbackFn, initialValue) : initialValue;
};
const reduceRequired = <SchemaShape extends Maybe<AnyObject>, ReturnResult>(
    accu: ReturnResult,
    [field, fieldSchema]: [string, Yup.AnySchema<SchemaShape>]
) => {
    const prop = field as keyof ReturnResult;
    if (isArraySchema<SchemaShape>(fieldSchema) && fieldSchema.innerType) {
        accu[prop] = [makeRequired<SchemaShape>(fieldSchema.innerType as unknown as Yup.AnySchema<SchemaShape>)] as never;
    } else if (isObjectSchema<SchemaShape>(fieldSchema)) {
        accu[prop] = makeRequired<SchemaShape>(fieldSchema as Yup.AnySchema<SchemaShape>) as never;
    } else if (fieldSchema.type !== 'lazy') {
        accu[prop] = Boolean(fieldSchema?.describe().tests?.some((t) => t.name === 'required')) as never;
    }
    return accu;
};

const makeRequired = <SchemaShape extends Maybe<AnyObject>>(schema: Yup.AnySchema<SchemaShape>): RequiredFields<SchemaShape> => {
    const res = {} as RequiredFields<SchemaShape>;
    return iterateYupFields<SchemaShape, RequiredFields<SchemaShape>>(schema, reduceRequired, res);
};

const reduceLabels = <SchemaShape extends Maybe<AnyObject>, ReturnResult>(
    accu: ReturnResult,
    [field, fieldSchema]: [string, Yup.AnySchema<SchemaShape>]
): ReturnResult => {
    const prop = field as keyof ReturnResult;
    if (isArraySchema<SchemaShape>(fieldSchema) && fieldSchema.innerType) {
        accu[prop] = [makeLabels<SchemaShape>(fieldSchema.innerType as unknown as Yup.AnySchema<SchemaShape>)] as never;
    } else if (isObjectSchema<SchemaShape>(fieldSchema)) {
        accu[prop] = makeLabels<SchemaShape>(fieldSchema) as never;
    } else if (fieldSchema.type !== 'lazy') {
        accu[prop] = fieldSchema.describe().label as never;
    }
    return accu;
};

const makeLabels = <SchemaShape extends Maybe<AnyObject>>(schema: Yup.AnySchema<SchemaShape>): FieldLabels<SchemaShape> => {
    const res = {} as FieldLabels<SchemaShape>;
    return iterateYupFields(schema, reduceLabels, res);
};

const makeFieldNames = <SchemaShape extends Maybe<AnyObject>>(schema: Yup.AnySchema<SchemaShape>, parent?: string): FieldNames<SchemaShape> => {
    const res = {} as FieldNames<SchemaShape>;
    return iterateYupFields(
        schema,
        (accu, [field, fieldSchema]) => {
            const prop = field as keyof FieldNames<SchemaShape>;
            const prefix = parent ? `${parent}.` : '';
            const name = `${prefix}${field}`;
            if (isArraySchema<SchemaShape>(fieldSchema) && fieldSchema.innerType) {
                accu[prop] = [makeFieldNames<SchemaShape>(fieldSchema.innerType as unknown as Yup.AnySchema<SchemaShape>)] as never;
            } else if (isObjectSchema<SchemaShape>(fieldSchema)) {
                accu[prop] = makeFieldNames<SchemaShape>(fieldSchema, name) as never;
            } else if (fieldSchema.type !== 'lazy') {
                accu[prop] = name as never;
            }
            return accu;
        },
        res
    );
};

const makeFieldProps = <SchemaShape extends Maybe<AnyObject>>({
    schema,
    required,
    labels,
    names,
}: {
    schema: Yup.AnySchema<SchemaShape>;
    required: RequiredFields<SchemaShape>;
    labels: FieldLabels<SchemaShape>;
    names: FieldNames<SchemaShape>;
}): FieldProps<SchemaShape> => {
    const res = {} as FieldProps<SchemaShape>;
    return iterateYupFields(
        schema,
        (props, [field, fieldSchema]) => {
            const prop = field as keyof typeof props;
            if (isArraySchema<SchemaShape>(fieldSchema) && fieldSchema.innerType) {
                props[prop] = [
                    makeFieldProps<SchemaShape>({
                        schema: fieldSchema?.innerType as unknown as Yup.AnySchema<SchemaShape>,
                        required: (required?.[prop] as unknown as Array<RequiredFields<SchemaShape>>)[0],
                        labels: (labels?.[prop] as unknown as Array<FieldLabels<SchemaShape>>)[0],
                        names: (names?.[prop] as unknown as Array<FieldNames<SchemaShape>>)[0],
                    }),
                ] as never;
            } else if (isObjectSchema<SchemaShape>(fieldSchema)) {
                props[prop] = makeFieldProps({
                    schema: fieldSchema,
                    required: required?.[prop] as unknown as RequiredFields<SchemaShape>,
                    labels: labels?.[prop] as unknown as FieldLabels<SchemaShape>,
                    names: names?.[prop] as unknown as FieldNames<SchemaShape>,
                }) as never;
            } else if (fieldSchema.type !== 'lazy') {
                props[prop] = new FieldProp({
                    required: required?.[prop] as unknown as boolean,
                    label: labels?.[prop] as unknown as string,
                    name: names?.[prop] as unknown as string,
                }) as never;
            }
            return props;
        },
        res
    );
};

const arrayFieldProp = <SchemaShape>(props: FieldProps<SchemaShape> | FieldProp, renameFn: (name: string) => string) => {
    let result: FieldProps<SchemaShape> | FieldProp;
    if (props instanceof FieldProp) {
        result = new FieldProp({
            ...props,
            name: renameFn(props.name),
        });
    } else {
        result = Object.entries(props).reduce((data, [field, dataProps]) => {
            const prop = field as keyof FieldProps<SchemaShape>;
            data[prop] = arrayFieldProp(dataProps as FieldProps<SchemaShape>, renameFn) as never;
            return data;
        }, {} as FieldProps<SchemaShape>);
    }
    return result;
};

const getArrayFieldProps = <FieldSchemaShape, ParentSchemaShape>(name: string, fieldProps: FieldProps<ParentSchemaShape>) => {
    const propName = name.replace(/\[.*]$/, '');
    const props = first(get(fieldProps, propName)) as FieldProps<ParentSchemaShape>;
    return props
        ? Object.entries(props).reduce((result, [field, propData]) => {
              result[field as keyof FieldProps<FieldSchemaShape>] = arrayFieldProp(
                  propData as FieldProps<ParentSchemaShape> | FieldProp,
                  (input) => `${name}.${input}`
              ) as never;
              return result;
          }, {} as FieldProps<FieldSchemaShape>)
        : null;
};

const createRffSchema = <SchemaShape extends AnyObject>(
    initialValues: SchemaShape,
    validationSchema: Yup.AnySchema<SchemaShape>
): RffSchema<SchemaShape> => {
    const data = {
        validationSchema,
        initialValues,
        validate: makeValidateSync<SchemaShape>(validationSchema),
        required: makeRequired<SchemaShape>(validationSchema),
        labels: makeLabels<SchemaShape>(validationSchema),
        names: makeFieldNames<SchemaShape>(validationSchema),
    };
    const fieldProps = makeFieldProps<SchemaShape>({ schema: validationSchema, ...data });
    return {
        ...data,
        fieldProps,
        arrayFieldProps: <FieldSchemaShape>(name: string) => getArrayFieldProps<FieldSchemaShape, SchemaShape>(name, fieldProps),
    };
};

export default createRffSchema;
